import { css } from 'canoe';
import { Form, Formik } from 'formik';
import {
  connectWithHammock,
  createCacheKey,
  ENDPOINTS,
  fetchResource,
  useCreatePayload,
} from 'hammock';
import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';

import { useNotification } from '@candu-ai/users/notification';
import { RuleGroup } from 'dashboard/pages/Segment/components/RuleGroup';

import { adjustInitialValues, parseSegmentValues } from './utils';
import { validationSchema } from './validationSchema';

const FormActions = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    margin-top: ${theme.spaces.xl};
  `}
`;

export interface DefinitionFormProps {
  resource: { [key: string]: any };
  onUpdate?: () => void;
  readOnly?: boolean;
  children?: React.ReactNode;
  segmentId: string;
}

export const DefinitionFormImpl = ({
  resource,
  onUpdate,
  readOnly,
  children,
  segmentId,
}: DefinitionFormProps) => {
  const { addNotification } = useNotification();
  const [updateBody, setUpdateBody] = useState<Record<string, any>>({});
  const { get: getSegmentsDefinition, getAll: getAllSegmentsDefinition, update } = useCreatePayload(
    ENDPOINTS.segmentsDefinition,
  );
  const { get: getSegmentAnalytics } = useCreatePayload(ENDPOINTS.segmentAnalytics);
  const { get: getSegmentUsers } = useCreatePayload(ENDPOINTS.segmentUsers);
  const queryClient = useQueryClient();

  const initialValues = useMemo(() => {
    return { definition: adjustInitialValues(resource.definition) };
  }, [resource, adjustInitialValues]);

  const sendEventPayload = useCallback((body: any) => update({ id: body.id, body }), []);

  const { mutate: updateSegment } = useMutation(
    ['update-segment'],
    () => fetchResource(sendEventPayload(updateBody)),
    {
      onSuccess: () => {
        addNotification({
          intent: 'success',
          message: 'Segment updated',
        });

        const segmentsDefinitionCacheKey = createCacheKey(getSegmentsDefinition({ id: segmentId }));

        queryClient.invalidateQueries(segmentsDefinitionCacheKey);

        const allSegmentsDefinitionCacheKey = createCacheKey(getAllSegmentsDefinition({}));

        queryClient.invalidateQueries(allSegmentsDefinitionCacheKey);

        const segmentAnalyticsCacheKey = createCacheKey(getSegmentAnalytics({ id: updateBody.id }));

        queryClient.invalidateQueries(segmentAnalyticsCacheKey);

        const segmentUsersActionFetch = getSegmentUsers({
          id: updateBody.id,
          query: { finder: 'bySegmentId', segmentId: updateBody.id, limit: 20, offset: 0 },
        });
        const segmentUsersCacheKey = createCacheKey(segmentUsersActionFetch);

        queryClient.invalidateQueries(segmentUsersCacheKey);

        onUpdate?.();
      },
      onError: async (e: any) => {
        const data = await e.json();
        addNotification({ intent: 'danger', message: data.message });
      },
    },
  );

  return (
    <div data-cy="definition-form-wrapper">
      <Formik
        initialValues={initialValues}
        validateOnBlur={false}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          setUpdateBody({ ...resource, definition: parseSegmentValues(values.definition) });
          updateSegment();
        }}
        enableReinitialize
      >
        <Form>
          <RuleGroup readOnly={readOnly} />
          <FormActions data-cy="segment-definition-form-actions">{children}</FormActions>
        </Form>
      </Formik>
    </div>
  );
};

export const DefinitionForm = connectWithHammock(({ segmentId }) => ({
  endpoint: ENDPOINTS.segmentsDefinition,
  id: segmentId,
}))(DefinitionFormImpl);
